import React, { useState } from 'react';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
} from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface Copy {
  text: string;
}

export interface ActivatedOfferCardProps extends PressableAnalyticsProps {
  offerId: string;
  avatarUrl: string;
  title: Copy;
  subTitle: Copy;
  sectionTitle?: Copy;
  tag?: TagProps;
  onClick: () => void;
  isDisabled?: boolean;
  accessibilityLabel?: string;
  testID?: string;
  accessibilityHint?: string;
}

export const ActivatedOfferCard = ({
  offerId: _offerId,
  avatarUrl,
  title,
  subTitle,
  tag,
  onClick,
  isDisabled = false,
  accessibilityLabel,
  testID,
  accessibilityHint,
  analyticsId = '',
  customAttributes = null,
}: ActivatedOfferCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const getTitleStyle = (): StyleProp<TextStyle> => {
    return isDisabled ? styles.titleDisabled : styles.title;
  };

  const getSubTitleStyle = (): StyleProp<TextStyle> => {
    return isDisabled ? styles.subTitleDisabled : styles.subTitle;
  };

  const handleOnPress = () => {
    if (!isDisabled) {
      cardPressAnalytics();
      onClick();
    }
  };

  return (
    <Pressable
      onPress={handleOnPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      style={[
        styles.container,
        isFocused && styles.containerFocused,
        isHovered && styles.containerHover,
        isPressed && styles.containerPressed,
        isDisabled && styles.containerDisabled,
      ]}
      disabled={isDisabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityRole="button"
      testID={testID}
    >
      <View style={styles.contentContainer}>
        <View style={styles.avatarSection}>
          <IconAvatar
            variant="MD"
            backgroundColor={theme.color.background.tertiary}
            defaultPlaceholderIcon="STORE"
            imageSource={{ uri: avatarUrl }}
            iconAccessibilityLabel="offer-logo-image"
          />
        </View>

        <View style={styles.textContent}>
          <View style={styles.titleContainer}>
            <BodyXSText
              style={getTitleStyle()}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {title.text}
            </BodyXSText>
          </View>

          <BodyBaseBoldText
            style={getSubTitleStyle()}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {subTitle.text}
          </BodyBaseBoldText>
        </View>

        {tag ? (
          <View>
            <Tag {...tag} />
          </View>
        ) : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  avatarSection: {
    marginRight: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.expressive.orchid,
    borderColor: 'transparent',
    borderRadius: theme.spacing.xs,
    margin: theme.spacing.m,
    minHeight: 72,
  },
  containerDisabled: {
    backgroundColor: theme.color.interactive.disabled.background,
    borderColor: theme.color.interactive.disabled.border,
    opacity: 0.6,
  },
  containerFocused: {
    backgroundColor: theme.color.expressive.orchid,
    borderColor: theme.color.border.focused,
    borderWidth: 1,
  },
  containerHover: {
    backgroundColor: theme.color.expressive.orchidHover,
    borderColor: theme.color.border.primarySolid,
  },
  containerPressed: {
    backgroundColor: theme.color.expressive.orchidPressed,
    borderColor: theme.color.border.primarySolid,
  },
  contentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },
  subTitle: {
    color: theme.color.text.primary,
  },
  subTitleDisabled: {
    color: theme.color.interactive.disabled.text,
  },
  textContent: {
    flex: 1,
    marginRight: theme.spacing.s,
  },
  title: {
    color: theme.color.text.primary,
    flex: 1,
  },
  titleContainer: {
    flexDirection: 'row',
  },
  titleDisabled: {
    color: theme.color.interactive.disabled.text,
    flex: 1,
  },
});

export default ActivatedOfferCard;
